import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  GraduationCap,
  BookOpen,
  Award,
  Briefcase,
  MapPin,
  FileSearch,
  Phone,
  Mail,
  ArrowRight,
} from "lucide-react";
import SectionHeading from "../components/SectionHeading";
import EducationInquiryModal from "../components/EducationInquiryModal";
import { DIVISIONS } from "../lib/divisions";
import { EDUCATION_PROGRAMS_LIST } from "../lib/educationPrograms";

const programIcons = {
  "vre": FileSearch,
  "gi-bill": Award,
  "college-planning": BookOpen,
  "trade-school": Briefcase,
  "career-transition": MapPin,
  "scholarships": GraduationCap,
};

const steps = [
  { n: "1", title: "Identify Goals", desc: "Where do you want to be — and by when?" },
  { n: "2", title: "Review Benefits", desc: "Understand what programs and funding you qualify for." },
  { n: "3", title: "Compare Programs", desc: "Weigh schools, trades, and pathways side-by-side." },
  { n: "4", title: "Prepare Applications", desc: "Get help with essays, paperwork, and deadlines." },
  { n: "5", title: "Build Your Career Plan", desc: "Turn education into a long-term career strategy." },
];

const EducationPage = () => {
  const div = DIVISIONS.education;
  const [showModal, setShowModal] = useState(false);

  return (
    <div className="pt-24">
      {/* Hero */}
      <section className="relative bg-white py-20 md:py-28 overflow-hidden">
        <div className="absolute top-1/4 left-0 w-[400px] h-[400px] bg-[#C89B3C]/10 rounded-full blur-3xl" />
        <div className="max-w-6xl mx-auto px-6 md:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="h-[3px] w-14 bg-[#C89B3C] mb-6" />
            <span className="text-xs font-medium tracking-[0.2em] uppercase text-slate-500 mb-3 block">
              Education Assistance
            </span>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-[#0B2343] leading-tight mb-6 max-w-3xl">
              Turn benefits into a career plan.
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed max-w-2xl mb-8">
              From VR&E walkthroughs and GI Bill guidance to college, trade, and scholarship
              planning — we help you translate your service and education benefits into a path
              forward.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => setShowModal(true)}
                className="bg-[#0B2343] text-white px-8 py-4 font-medium hover:bg-slate-800 transition-all inline-flex items-center justify-center gap-2"
                data-testid="education-inquiry-cta"
              >
                Request Guidance
                <ArrowRight size={18} />
              </button>
              <Link
                to="/schedule"
                className="border-2 border-[#0B2343] text-[#0B2343] hover:bg-[#0B2343] hover:text-white px-8 py-4 font-medium transition-all inline-flex items-center justify-center gap-2"
              >
                Schedule Consultation
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Program grid */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <SectionHeading
            eyebrow="Our Programs"
            title="Guidance every step of the way."
            align="center"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {EDUCATION_PROGRAMS_LIST.map((p, idx) => {
              const Icon = programIcons[p.slug] || BookOpen;
              return (
                <motion.div
                  key={p.slug}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.5, delay: idx * 0.06 }}
                  className="group bg-white p-6 border border-slate-200 hover:border-[#C89B3C] hover:shadow-lg transition-all flex flex-col"
                  data-testid={`education-program-${p.slug}`}
                >
                  <div className="w-12 h-12 bg-[#0B2343] flex items-center justify-center mb-4 group-hover:bg-[#C89B3C] transition-colors">
                    <Icon className="text-[#C89B3C] group-hover:text-white transition-colors" size={22} />
                  </div>
                  <h3 className="font-serif text-lg font-bold text-slate-900 mb-2">{p.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed mb-5 flex-1">{p.tagline}</p>
                  <Link
                    to={`/education/${p.slug}`}
                    className="inline-flex items-center gap-2 text-[#0B2343] font-medium text-sm hover:text-[#C89B3C] transition-colors border-t border-slate-200 pt-4"
                    data-testid={`education-program-link-${p.slug}`}
                  >
                    Learn more <ArrowRight size={14} />
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 5-Step Process */}
      <section className="py-24 bg-[#0B2343] text-white">
        <div className="max-w-6xl mx-auto px-6 md:px-8">
          <SectionHeading
            eyebrow="Our Process"
            title="A simple five-step path forward."
            align="center"
            light
          />
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {steps.map((s, idx) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5, delay: idx * 0.08 }}
                className="bg-white/5 border border-white/10 p-5 hover:border-[#C89B3C]/60 transition-colors"
              >
                <span className="font-mono text-[#C89B3C] text-sm">Step {s.n}</span>
                <h3 className="font-serif font-bold text-white mt-1 mb-2">{s.title}</h3>
                <p className="text-slate-300 text-sm">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-8 text-center">
          <div className="h-[3px] w-14 bg-[#C89B3C] mx-auto mb-6" />
          <h2 className="font-serif text-2xl md:text-3xl font-bold text-slate-900 mb-6">
            Let's map out your next step.
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => setShowModal(true)}
              className="bg-[#C89B3C] text-white px-8 py-4 font-medium hover:bg-[#a8852f] transition-all inline-flex items-center justify-center gap-2"
              data-testid="education-cta-bottom"
            >
              Request Guidance
              <ArrowRight size={18} />
            </button>
            <a
              href={div.phoneHref}
              className="border-2 border-[#0B2343] text-[#0B2343] hover:bg-[#0B2343] hover:text-white px-8 py-4 font-medium transition-all inline-flex items-center justify-center gap-2"
            >
              <Phone size={18} />
              {div.phone}
            </a>
          </div>
          <a
            href={`mailto:${div.email}`}
            className="mt-6 text-slate-600 hover:text-[#C89B3C] inline-flex items-center gap-2 text-sm"
          >
            <Mail size={14} />
            {div.email}
          </a>
        </div>
      </section>

      <EducationInquiryModal isOpen={showModal} onClose={() => setShowModal(false)} />
    </div>
  );
};

export default EducationPage;
